import ServicePageLayout from './ServicePageLayout';
import { Crown, Brain, MessageSquare, Scale, FolderOpen, TrendingUp, Shield, Settings, Users, Clock } from 'lucide-react';

const relatedServices = [
  { path: '/conseil-rh/', label: 'Conseil RH Particuliers' },
  { path: '/droit-social/', label: 'Expertise Droit Social' },
  { path: '/audit-rh/', label: 'Audit RH' },
  { path: '/accompagnement-collectivites/', label: 'Accompagnement Collectivités' },
];

const formations = [
  {
    icon: Crown,
    title: 'Management et leadership',
    description: 'Développez votre posture de leader et apprenez à manager vos équipes avec efficacité. Cette formation couvre le management d\'équipe au quotidien, la communication managériale, le feedback constructif et la prévention des tensions. Des mises en situation concrètes pour un apprentissage immédiatement applicable.',
    duree: '2 à 3 jours',
    public: 'Managers, chefs d\'équipe, dirigeants',
  },
  {
    icon: Brain,
    title: 'Gestion du stress et prévention des RPS',
    description: 'Comprenez les mécanismes du stress au travail et apprenez à identifier les signaux d\'alerte individuels et collectifs. Cette formation vous donne des outils concrets de régulation du stress et vous sensibilise aux risques psychosociaux (RPS) et aux obligations de l\'employeur en la matière.',
    duree: '1 à 2 jours',
    public: 'Tous salariés, managers, RH',
  },
  {
    icon: MessageSquare,
    title: 'Communication professionnelle',
    description: 'Maîtrisez la communication assertive et l\'écoute active pour améliorer vos relations de travail. Apprenez à gérer les désaccords, à travailler sur vos comportements et postures professionnels, et à améliorer la coopération interservices au sein de votre organisation.',
    duree: '1 à 2 jours',
    public: 'Tous salariés, équipes projet',
  },
  {
    icon: Scale,
    title: 'Droit du travail pour managers',
    description: 'Formation essentielle pour tout manager : rôles et responsabilités, gestion du temps de travail et des absences, bonnes pratiques en matière de sanctions et procédures disciplinaires. Prévenez les risques juridiques et contentieux grâce à une compréhension solide du cadre légal.',
    duree: '1 à 3 jours',
    public: 'Managers, dirigeants, RH opérationnels',
  },
  {
    icon: FolderOpen,
    title: 'Gestion RH pour non-spécialistes',
    description: 'Les fondamentaux RH simplifiés pour les managers et entrepreneurs sans formation RH. Contrats, obligations légales, documents clés, organisation RH simple et efficace, lecture des bulletins de paie, et anticipation des risques sociaux. Devenez autonome sur le quotidien RH.',
    duree: '2 jours',
    public: 'Managers, entrepreneurs, TPE',
  },
  {
    icon: TrendingUp,
    title: 'Prise de poste et évolution professionnelle',
    description: 'Préparez-vous à intégrer un nouveau poste ou à évoluer vers des responsabilités accrues. Cette formation couvre l\'organisation du travail, la priorisation, le développement de la confiance professionnelle et la préparation à une prise de responsabilité réussie.',
    duree: '1 à 2 jours',
    public: 'Nouveaux managers, talents à potentiel',
  },
  {
    icon: Shield,
    title: 'Prévention et gestion des conflits',
    description: 'Comprenez les mécanismes du conflit et apprenez les techniques de désamorçage et de médiation de premier niveau. Développez une posture managériale adaptée face aux tensions et apprenez à construire des solutions durables pour préserver l\'harmonie en équipe.',
    duree: '1 à 2 jours',
    public: 'Managers, RH, référents sociaux',
  },
  {
    icon: Settings,
    title: 'Formations sur-mesure intra-entreprise',
    description: 'Chaque organisation est unique. J\'analyse vos besoins spécifiques et vos objectifs pour concevoir une formation entièrement personnalisée. Le contenu est adapté à votre public, avec des cas pratiques issus de votre entreprise, et inclut une évaluation et un ajustement post-formation.',
    duree: 'Sur mesure',
    public: 'Toutes équipes et organisations',
  },
];

export default function FormationRHPage() {
  return (
    <ServicePageLayout
      title="Formations RH et Management sur Mesure | Aurore RH"
      description="Formations professionnelles en management, droit du travail et communication. Sur-mesure, inter ou intra entreprise."
      canonical="https://aurorerh.fr/formation-rh/"
      relatedServices={relatedServices}
    >
      <div className="prose dark:prose-invert max-w-none mb-16">
        <p className="text-lg text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed mb-6">
          La formation est un levier puissant pour développer les compétences de vos équipes, améliorer la performance 
          de votre organisation et fidéliser vos collaborateurs. En tant que formatrice indépendante, je conçois et 
          anime des formations pragmatiques, interactives et immédiatement applicables au quotidien professionnel.
        </p>
        <p className="text-lg text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed mb-6">
          Mon approche pédagogique privilégie l'expérientiel : je crois profondément que l'on apprend en faisant. 
          Chaque formation intègre des mises en situation, des études de cas concrets et des exercices pratiques 
          pour garantir l'ancrage des apprentissages. Les participants repartent avec des outils qu'ils peuvent 
          utiliser dès le lendemain.
        </p>
      </div>

      <h2 className="text-3xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-10">
        Notre catalogue de formations
      </h2>

      <div className="space-y-8 mb-16">
        {formations.map((formation, index) => (
          <div
            key={index}
            className="p-6 bg-white dark:bg-warm-gray-800 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-aurore-light/20 dark:border-warm-gray-700"
          >
            <div className="flex gap-5 mb-4">
              <div className="flex-shrink-0 w-14 h-14 bg-aurore-light/40 dark:bg-warm-gray-700 rounded-xl flex items-center justify-center">
                <formation.icon className="w-7 h-7 text-aurore-dark dark:text-aurore-light" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-warm-gray-900 dark:text-warm-gray-100 mb-2">
                  {formation.title}
                </h3>
                <p className="text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed mb-4">
                  {formation.description}
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2 text-warm-gray-600 dark:text-warm-gray-400">
                    <Clock className="w-4 h-4" />
                    <span>{formation.duree}</span>
                  </div>
                  <div className="flex items-center gap-2 text-warm-gray-600 dark:text-warm-gray-400">
                    <Users className="w-4 h-4" />
                    <span>{formation.public}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <h2 className="text-3xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-6">
        Modalités de formation
      </h2>
      <div className="grid sm:grid-cols-3 gap-6 mb-16">
        {[
          {
            title: 'Inter-entreprises',
            desc: 'Rejoignez une session ouverte avec des participants d\'horizons variés. Opportunité d\'échanges riches et de networking.',
          },
          {
            title: 'Intra-entreprise',
            desc: 'Formation dédiée à votre équipe, dans vos locaux ou en visioconférence. Contenu adapté à votre contexte spécifique.',
          },
          {
            title: 'Coaching individuel',
            desc: 'Accompagnement personnalisé en one-to-one pour des besoins très spécifiques ou confidentiels.',
          },
        ].map((item, i) => (
          <div key={i} className="p-6 bg-warm-gray-50 dark:bg-warm-gray-800/50 rounded-xl text-center">
            <h3 className="font-semibold text-warm-gray-900 dark:text-warm-gray-100 mb-3">{item.title}</h3>
            <p className="text-warm-gray-700 dark:text-warm-gray-300 text-sm">{item.desc}</p>
          </div>
        ))}
      </div>

      <h2 className="text-3xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-6">
        Notre méthode pédagogique
      </h2>
      <div className="space-y-4 mb-16">
        {[
          'Analyse des besoins spécifiques de votre organisation avant la formation',
          'Conception sur-mesure du contenu pédagogique avec validation conjointe',
          'Animation interactive privilégiant les mises en situation et l\'expérimentation',
          'Support de cours complet et outils pratiques remis à chaque participant',
          'Évaluation à chaud et à froid pour mesurer l\'impact de la formation',
          'Suivi post-formation et possibilité de session de consolidation',
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 bg-aurore-dark text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">
              {i + 1}
            </div>
            <p className="text-warm-gray-700 dark:text-warm-gray-300">{item}</p>
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-br from-aurore-pale to-aurore-light/30 dark:from-warm-gray-800 dark:to-warm-gray-800 rounded-2xl p-8">
        <h3 className="text-xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-4">
          Formations éligibles au CPF et OPCO
        </h3>
        <p className="text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed">
          De nombreuses formations sont éligibles au Compte Personnel de Formation (CPF) et peuvent être financées 
          par les OPCO. Je vous accompagne dans les démarches de financement pour optimiser votre budget formation 
          et celui de vos collaborateurs.
        </p>
      </div>
    </ServicePageLayout>
  );
}
